import { Box, Stack } from "@mui/material";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { parseAsStringEnum, useQueryState } from "nuqs";
import { useMemo } from "react";

import { useTableSearchParams } from "Forms/Hooks/useTableSearchParams";
import { getModuleStatusOptions } from "Forms/Utils/EnumOptions";
import {
  ModuleListSortColumn,
  ModuleStatus,
  SortOrder,
} from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useModulesQuery } from "Modules/Api/Queries/useModulesQuery";
import {
  MODULES_SORTABLE_COLUMNS,
  useModulesTableColumns,
} from "Modules/Components/useModulesTableColumns";
import { TableSearchInput } from "Projects/Components/TableSearchInput";
import { BlTanstackTable } from "Shared/DataTable/BlTanstackTable";
import { BlDropdown } from "Shared/Inputs/BlDropdown";
import type { BlOption } from "Shared/Inputs/BlOption";

const SORT_COLUMN_MAP: Record<string, ModuleListSortColumn> = {
  name: ModuleListSortColumn.Name,
  type: ModuleListSortColumn.Type,
  projectCount: ModuleListSortColumn.ProjectCount,
};

export const ModulesTable = (): React.ReactNode => {
  const { t } = useResource();

  const [selectedStatus, setSelectedStatus] = useQueryState(
    "status",
    parseAsStringEnum<ModuleStatus>(Object.values(ModuleStatus)),
  );

  const {
    search,
    debouncedSearch,
    onSearchChange,
    pageIndex,
    pageSize,
    onPaginationChange,
    resetPage,
    sorting,
    onSortingChange,
  } = useTableSearchParams({ validSortIds: [...MODULES_SORTABLE_COLUMNS] });

  const sortBy = sorting[0]?.id ? SORT_COLUMN_MAP[sorting[0].id] : undefined;
  const sortOrder = sorting[0]?.desc
    ? SortOrder.Descending
    : SortOrder.Ascending;

  const { data: modulesData, isLoading } = useModulesQuery({
    offset: pageIndex * pageSize,
    limit: pageSize,
    searchQuery: debouncedSearch || undefined,
    statusFilter: selectedStatus ? [selectedStatus] : undefined,
    sortBy,
    sortOrder,
  });

  const modules = modulesData?.items ?? [];
  const totalCount = modulesData?.totalCount ?? 0;

  const statusOptions = useMemo(
    (): BlOption[] => [
      { value: "", label: t(Resources.Common.AllStatuses) },
      ...getModuleStatusOptions(),
    ],
    [t],
  );

  const columns = useModulesTableColumns();

  const table = useReactTable({
    data: modules,
    columns,
    getRowId: (row) => row.moduleID,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    rowCount: totalCount,
    state: { pagination: { pageIndex, pageSize }, sorting },
    onSortingChange,
    onPaginationChange,
  });

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={Spacing.M}
        sx={{ marginBottom: Spacing.M }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <TableSearchInput
            name="module-search"
            value={search}
            onChange={onSearchChange}
            placeholder={t(Resources.Modules.SearchModulesPlaceholder)}
          />
        </Box>
        <Box sx={{ minWidth: 200 }}>
          <BlDropdown
            name="status-filter"
            options={statusOptions}
            value={selectedStatus ?? ""}
            onChange={(value) => {
              setSelectedStatus(value ? (value as ModuleStatus) : null);
              resetPage();
            }}
            placeholder={t(Resources.Modules.Availability)}
          />
        </Box>
      </Stack>
      <BlTanstackTable
        table={table}
        isLoading={isLoading}
        totalCount={totalCount}
      />
    </Box>
  );
};
